import type React from "react";
import { useSceneLoop } from "@/components/motion/motion";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const MIGRATION_STATES = ["legacy", "migrating", "release", "handoff"] as const;
export type MigrationState = (typeof MIGRATION_STATES)[number];

const LOOP = ["legacy", "migrating", "release"] as const;
const LOOP_DURATIONS = [2400, 2600, 3600] as const;

const BEFORE_ROWS = [
	{ y: 24, w: 50, crossed: true },
	{ y: 38, w: 38, crossed: true },
	{ y: 52, w: 56, crossed: false },
	{ y: 66, w: 30, crossed: true },
];
const AFTER_ROWS = [
	{ y: 24, w: 56, opacity: 1 },
	{ y: 38, w: 44, opacity: 0.75 },
	{ y: 52, w: 60, opacity: 0.6 },
	{ y: 66, w: 36, opacity: 0.45 },
];

/** Self-playing loop: legacy → migrating → release. Server-renders "release". */
export function MigrationSceneLoop({ className }: { className?: string }) {
	const { ref, index } = useSceneLoop(LOOP_DURATIONS, { initial: 2 });
	return (
		<div ref={ref} className={className}>
			<MigrationScene state={LOOP[index] ?? "release"} />
		</div>
	);
}

/** Legacy React Native → Expo, then the release pipeline lights up. */
export function MigrationScene({
	state,
	className,
}: {
	state: MigrationState;
	className?: string;
}) {
	const { t } = useI18n();
	const i = (n: number) => ({ "--i": n }) as React.CSSProperties;

	return (
		<svg
			viewBox="0 0 230 150"
			className={cn("block h-auto w-full", className)}
			data-state={state}
			aria-hidden="true"
		>
			{/* before */}
			<rect
				x="1"
				y="1"
				width="86"
				height="82"
				rx="7"
				strokeWidth=".8"
				style={{ fill: "var(--paper-3)", stroke: "var(--rule-2)" }}
			/>
			<text
				x="10"
				y="15"
				fontFamily="IBM Plex Mono"
				fontSize="6"
				letterSpacing=".08em"
				style={{ fill: "var(--ink-4)" }}
			>
				{t((t) => t.scenes.migration.before).toUpperCase()}
			</text>
			{BEFORE_ROWS.map((row, n) => (
				<g key={row.y}>
					<rect
						x="10"
						y={row.y}
						width={row.w}
						height="9"
						rx="2.5"
						style={{ fill: "var(--rule-2)" }}
					/>
					{row.crossed ? (
						<>
							<line
								x1="10"
								y1={row.y + 4.5}
								x2={10 + row.w}
								y2={row.y + 4.5}
								strokeWidth=".8"
								style={{ stroke: "var(--ink-4)" }}
							/>
							<path
								className="mig-cross"
								d={`M70 ${row.y + 2} l5 5 M75 ${row.y + 2} l-5 5`}
								strokeWidth="1.5"
								strokeLinecap="round"
								style={{ ...i(n), stroke: "var(--sky-ink)" }}
							/>
						</>
					) : null}
				</g>
			))}

			{/* arrow */}
			<g className="mig-arrow">
				<path
					d="M95 42 h30 M119 36 l6 6 l-6 6"
					fill="none"
					strokeWidth="1.6"
					strokeLinecap="round"
					strokeLinejoin="round"
					style={{ stroke: "var(--lilac-ink)" }}
				/>
				<text
					x="110"
					y="58"
					fontFamily="IBM Plex Mono"
					fontSize="5.6"
					textAnchor="middle"
					style={{ fill: "var(--lilac-ink)" }}
				>
					{t((t) => t.scenes.migration.arrow)}
				</text>
			</g>

			{/* after */}
			<rect
				x="133"
				y="1"
				width="96"
				height="82"
				rx="7"
				strokeWidth=".8"
				style={{
					fill: "var(--scene-lilac-wash)",
					stroke: "var(--scene-lilac-2)",
				}}
			/>
			<text
				x="142"
				y="15"
				fontFamily="IBM Plex Mono"
				fontSize="6"
				letterSpacing=".08em"
				style={{ fill: "var(--lilac-ink)" }}
			>
				{t((t) => t.scenes.migration.after).toUpperCase()}
			</text>
			{AFTER_ROWS.map((row, n) => (
				<g key={row.y}>
					<rect
						className="mig-row"
						x="142"
						y={row.y}
						width={row.w}
						height="9"
						rx="2.5"
						opacity={row.opacity}
						style={{ ...i(n), fill: "var(--lilac-ink)" }}
					/>
					<path
						className="mig-check"
						d={`M210 ${row.y + 4.5} l2.5 2.5 l5 -5`}
						fill="none"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
						style={{ ...i(n + 1), stroke: "var(--scene-sky-3)" }}
					/>
				</g>
			))}

			{/* release pipeline */}
			<text
				x="115"
				y="98"
				fontFamily="IBM Plex Mono"
				fontSize="5.6"
				letterSpacing=".08em"
				textAnchor="middle"
				style={{ fill: "var(--ink-4)" }}
			>
				{t((t) => t.scenes.migration.pipeline).toUpperCase()}
			</text>
			<line
				x1="12"
				y1="108"
				x2="218"
				y2="108"
				strokeWidth="1"
				strokeDasharray="2 2.5"
				opacity=".5"
				style={{ stroke: "var(--lilac-ink)" }}
			/>
			<rect
				className="mig-progress"
				x="12"
				y="107.3"
				width="206"
				height="1.4"
				style={{ ...i(1), fill: "var(--lilac-ink)" }}
			/>
			{[
				{ x: 12, label: t((t) => t.scenes.migration.build) },
				{ x: 115, label: t((t) => t.scenes.migration.release) },
				{ x: 218, label: t((t) => t.scenes.migration.stores), last: true },
			].map((dot, n) => (
				<g key={dot.x}>
					<circle
						className={cn("mig-dot", dot.last && "mig-dot--last")}
						cx={dot.x}
						cy="108"
						r="5"
						style={i(n + 2)}
					/>
					<text
						x={dot.x}
						y="125"
						fontFamily="IBM Plex Mono"
						fontSize="6"
						textAnchor="middle"
						style={{ fill: "var(--ink)" }}
					>
						{dot.label}
					</text>
				</g>
			))}

			{/* hand-over notes */}
			<g className="mig-doc">
				<rect
					x="150"
					y="128"
					width="76"
					height="20"
					rx="3"
					strokeWidth=".8"
					style={{ fill: "var(--scene-card)", stroke: "var(--rule-2)" }}
				/>
				<text
					x="157"
					y="140.5"
					fontFamily="IBM Plex Mono"
					fontSize="6"
					style={{ fill: "var(--ink)" }}
				>
					README.md
				</text>
				<path
					d="M208 138 l2.5 2.5 l5 -5"
					fill="none"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
					style={{ stroke: "var(--scene-sky-3)" }}
				/>
			</g>
		</svg>
	);
}
